/**
 * GameStateManager - Centralized state management with observer pattern
 * Manages game state and notifies subscribers of changes
 * Based on ADR-003: Game State Management Architecture
 */

import type { GameState } from '@/types';
import { createInitialGameState, makeMove as engineMakeMove, resetGame } from './GameEngine';

type StateListener = (state: GameState) => void;

/**
 * GameStateManager
 * Implements observer pattern for reactive UI updates
 */
export class GameStateManager {
  private state: GameState;
  private listeners: StateListener[] = [];

  constructor() {
    this.state = createInitialGameState();
  }

  /**
   * Get current game state (immutable)
   */
  public getState(): Readonly<GameState> {
    return Object.freeze({ ...this.state });
  }

  /**
   * Subscribe to state changes
   * Returns unsubscribe function
   */
  public subscribe(listener: StateListener): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Make a move at the specified position
   */
  public makeMove(row: number, col: number): void {
    const newState = engineMakeMove(this.state, row, col);
    
    // Only update and notify if state actually changed
    if (newState !== this.state) {
      this.state = newState;
      this.notify();
    }
  }

  /**
   * Reset game to initial state
   */
  public reset(): void {
    this.state = resetGame();
    this.notify();
  }

  /**
   * Notify all subscribers of state change
   */
  private notify(): void {
    const frozenState = this.getState();
    this.listeners.forEach((listener) => {
      try {
        listener(frozenState);
      } catch (error) {
        console.error('Error in state listener:', error);
      }
    });
  }
}
